import type { Card } from '../../types'
import { motion } from 'framer-motion'
import { ImageIcon } from 'lucide-react'
import { getScaledIconSize } from '../../utils/gridLayout'
import { ImageDithering, FlutedGlass, HalftoneDots } from '@paper-design/shaders-react'
import { useThemeStore, NEO_DITHER_COLORS, THEME_PALETTES } from '../../store/themeStore'
import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo, useState, useCallback, memo } from 'react'
import PhotoLightbox from '../PhotoLightbox'

const LOADED_IMAGE_SRCS = new Set<string>()

function hashToUint32(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function clampColor(value: number) {
  return Math.max(0, Math.min(255, Math.round(value)))
}

function normalizeHex(input: string) {
  const hex = input.trim().replace('#', '')
  if (hex.length === 3) {
    return `#${hex.split('').map((part) => `${part}${part}`).join('')}`
  }
  if (hex.length === 6) return `#${hex}`
  return '#000000'
}

function hexToRgb(input: string) {
  const hex = normalizeHex(input)
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  }
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${clampColor(r).toString(16).padStart(2, '0')}${clampColor(g).toString(16).padStart(2, '0')}${clampColor(b).toString(16).padStart(2, '0')}`
}

function mixHex(a: string, b: string, ratio: number) {
  const safeRatio = Math.max(0, Math.min(1, ratio))
  const from = hexToRgb(a)
  const to = hexToRgb(b)
  return rgbToHex(
    from.r + (to.r - from.r) * safeRatio,
    from.g + (to.g - from.g) * safeRatio,
    from.b + (to.b - from.b) * safeRatio,
  )
}

function getNeoDitherColors(themePaletteIndex: number, shadeIndex: number): [string, string, string] {
  const palette = THEME_PALETTES.neo[themePaletteIndex] || THEME_PALETTES.neo[0]
  const colors = palette.colors
  const safeShadeIndex = Math.max(0, Math.min(colors.length - 1, shadeIndex))
  const selected = colors[safeShadeIndex] || colors[0]
  const darkestTone = colors[0] || selected
  const darkerNeighbor = colors[Math.max(0, safeShadeIndex - 1)] || darkestTone
  const lighterNeighbor = colors[Math.min(colors.length - 1, safeShadeIndex + 1)] || colors[colors.length - 1]
  // Stronger tinting toward the card shade color for cohesive dither
  const strongBack = mixHex(mixHex(selected, darkerNeighbor, 0.4), '#000000', 0.55)
  const liftedFront = mixHex(selected, '#ffffff', safeShadeIndex >= colors.length - 1 ? 0.06 : 0.12)
  const liftedHighlight = mixHex(lighterNeighbor, '#ffffff', safeShadeIndex >= colors.length - 1 ? 0.28 : 0.35)

  if (safeShadeIndex === 0) {
    return [mixHex(selected, '#000000', 0.72), mixHex(selected, '#ffffff', 0.08), mixHex(lighterNeighbor, '#ffffff', 0.22)]
  }

  return [strongBack, liftedFront, liftedHighlight]
}

function getWoodHalftoneColors(themePaletteIndex: number, shadeIndex: number) {
  const palette = THEME_PALETTES.wood[themePaletteIndex] || THEME_PALETTES.wood[0]
  const colors = palette.colors
  const safeShadeIndex = Math.max(0, Math.min(colors.length - 1, shadeIndex))
  const selected = colors[safeShadeIndex] || colors[0]

  return {
    front: selected,
  }
}

interface PhotoCardProps {
  card: Card
  editMode: boolean
  cellSize: number
  themeOverride?: 'mono' | 'glass' | 'neo' | 'wood'
}

const PhotoCard = memo(function PhotoCard({ card, editMode, cellSize, themeOverride }: PhotoCardProps) {
  const { theme: storeTheme, colorPalette, paletteSeed } = useThemeStore()
  const theme = themeOverride ?? storeTheme
  const { imageUrl, photoLinkUrl, photoFilter = 'none', photoZoomDisabled = false } = card.content
  const iconSize = getScaledIconSize(cellSize, 32)

  const images = Array.isArray((card.content as any).images) ? ((card.content as any).images as string[]) : undefined
  const imagesKey = useMemo(() => (images && images.length ? images.join('|') : ''), [images])
  const effectiveImages = useMemo(() => {
    if (images && images.length > 0) return images
    return imageUrl ? [imageUrl] : []
  }, [imageUrl, imagesKey])
  const [currentIndex, setCurrentIndex] = useState(0)

  const [retryNonceByIndex, setRetryNonceByIndex] = useState<Record<number, number>>({})
  const [hasLoadedByIndex, setHasLoadedByIndex] = useState<Record<number, boolean>>({})
  const [hasErrorByIndex, setHasErrorByIndex] = useState<Record<number, boolean>>({})

  useEffect(() => {
    setCurrentIndex(0)
    setRetryNonceByIndex({})
    setHasLoadedByIndex({})
    setHasErrorByIndex({})
  }, [card.id, imageUrl, imagesKey])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (!effectiveImages.length) return
      const idx = currentIndex
      const loaded = !!hasLoadedByIndex[idx]
      if (loaded) return
      setRetryNonceByIndex((prev) => ({ ...prev, [idx]: Date.now() }))
      setHasErrorByIndex((prev) => ({ ...prev, [idx]: false }))
    }

    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [currentIndex, effectiveImages.length, hasLoadedByIndex])

  const goNext = useCallback(() => setCurrentIndex((prev) => (prev + 1) % effectiveImages.length), [effectiveImages.length])
  const goPrev = useCallback(() => setCurrentIndex((prev) => (prev - 1 + effectiveImages.length) % effectiveImages.length), [effectiveImages.length])

  const displaySrc = useMemo(() => {
    const raw = effectiveImages[currentIndex]
    if (!raw) return ''
    const nonce = retryNonceByIndex[currentIndex]
    if (!nonce) return raw
    const sep = raw.includes('?') ? '&' : '?'
    return `${raw}${sep}v=${nonce}`
  }, [currentIndex, effectiveImages, retryNonceByIndex])

  useEffect(() => {
    if (!displaySrc) return

    if (LOADED_IMAGE_SRCS.has(displaySrc)) {
      setHasLoadedByIndex((prev) => ({ ...prev, [currentIndex]: true }))
      setHasErrorByIndex((prev) => ({ ...prev, [currentIndex]: false }))
      return
    }

    const probe = new Image()
    probe.src = displaySrc
    if (probe.complete) {
      LOADED_IMAGE_SRCS.add(displaySrc)
      setHasLoadedByIndex((prev) => ({ ...prev, [currentIndex]: true }))
      setHasErrorByIndex((prev) => ({ ...prev, [currentIndex]: false }))
    }
  }, [currentIndex, displaySrc])

  const showSkeleton = effectiveImages.length > 0 && !hasLoadedByIndex[currentIndex]

  const filterOn = photoFilter === 'dither'
  const wantsDither = theme === 'neo' && filterOn && !hasErrorByIndex[currentIndex]
  const wantsFlutedGlass = theme === 'glass' && filterOn && !hasErrorByIndex[currentIndex]
  const wantsHalftoneDots = theme === 'wood' && filterOn && !hasErrorByIndex[currentIndex]
  const [ditherImageEl, setDitherImageEl] = useState<HTMLImageElement | null>(null)
  const [ditherReady, setDitherReady] = useState(false)
  const [showLightbox, setShowLightbox] = useState(false)

  const forcedPaletteIndexRaw = (card.content as any)?.paletteIndex
  const forcedPaletteIndex =
    forcedPaletteIndexRaw === 1 || forcedPaletteIndexRaw === 2 || forcedPaletteIndexRaw === 3 || forcedPaletteIndexRaw === 4
      ? forcedPaletteIndexRaw - 1
      : null

  const selectedShadeIndex = forcedPaletteIndex ?? (hashToUint32(`${card.id}-${paletteSeed}`) % 4)
  const ditherColors = theme === 'neo'
    ? getNeoDitherColors(colorPalette, selectedShadeIndex)
    : NEO_DITHER_COLORS[colorPalette] || NEO_DITHER_COLORS[0]

  const halftoneDotColors = getWoodHalftoneColors(colorPalette, selectedShadeIndex)
  const woodBackColor = useMemo(() => {
    if (typeof window === 'undefined') return '#141414'
    const value = getComputedStyle(document.documentElement).getPropertyValue('--app-bg').trim()
    return value || '#141414'
  }, [theme, colorPalette])

  useEffect(() => {
    if ((!wantsDither && !wantsFlutedGlass && !wantsHalftoneDots) || !displaySrc) {
      setDitherImageEl(null)
      setDitherReady(false)
      return
    }

    let cancelled = false
    setDitherReady(false)

    const tryLoad = (src: string, withCors: boolean, onFail?: () => void) => {
      const img = new Image()
      if (withCors) img.crossOrigin = 'anonymous'
      img.decoding = 'async'
      img.onload = () => {
        if (cancelled) return
        setDitherImageEl(img)
        setDitherReady(true)
      }
      img.onerror = () => {
        if (cancelled) return
        if (onFail) return onFail()
        setDitherImageEl(null)
        setDitherReady(false)
      }
      img.src = src
    }

    const canProxy = /^https?:\/\//i.test(displaySrc)
    const proxySrc = `/api/image-proxy?url=${encodeURIComponent(displaySrc)}`

    // First try: direct load (works in editor when R2 CORS allows it)
    // Fallback: proxy (works on public subdomains even if R2 CORS blocks canvas usage)
    tryLoad(displaySrc, true, () => {
      if (!canProxy) {
        setDitherImageEl(null)
        setDitherReady(false)
        return
      }
      tryLoad(proxySrc, false)
    })

    return () => {
      cancelled = true
    }
  }, [displaySrc, wantsDither, wantsFlutedGlass, wantsHalftoneDots])

  const normalizeExternalUrl = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed) return ''
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (editMode) {
      e.preventDefault()
      return
    }
    if (photoLinkUrl) {
      const external = normalizeExternalUrl(photoLinkUrl)
      if (external) window.open(external, '_blank', 'noopener,noreferrer')
    } else if (!photoZoomDisabled && effectiveImages.length > 0) {
      setShowLightbox(true)
    }
  }, [editMode, photoLinkUrl, photoZoomDisabled, effectiveImages.length])

  const cardWidth = Math.max(64, Math.round(card.grid_width * cellSize))
  const cardHeight = Math.max(64, Math.round(card.grid_height * cellSize))
  return (
    <>
      <motion.div
        className="w-full h-full theme-card theme-card-hover overflow-hidden"
        whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
        onClick={handleClick}
        style={{ transform: 'translateZ(0)' }}
      >
        {effectiveImages.length > 0 ? (
          <div className="w-full h-full relative">
            {showSkeleton && (
              <div className="absolute inset-0 bg-[var(--card-bg-2)] opacity-50">
                <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-[var(--app-fg)]/10 to-transparent" />
              </div>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={`${currentIndex}-${retryNonceByIndex[currentIndex] || 0}`}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: hasLoadedByIndex[currentIndex] ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                drag={effectiveImages.length > 1 ? 'x' : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.12}
                onDragStart={(e) => e.stopPropagation()}
                onDragEnd={(e, info) => {
                  e.stopPropagation()
                  if (effectiveImages.length <= 1) return
                  if (info.offset.x < -40) goNext()
                  else if (info.offset.x > 40) goPrev()
                }}
              >
                {/* Neo theme dither effect */}
                {wantsDither && ditherReady && ditherImageEl && (
                  <div className="absolute inset-0 pointer-events-none" style={{ padding: 0, transform: 'scale(1.02)' }}>
                    <ImageDithering
                      width={cardWidth}
                      height={cardHeight}
                      image={ditherImageEl}
                      colorBack={ditherColors[0]}
                      colorFront={ditherColors[1]}
                      colorHighlight={ditherColors[2]}
                      originalColors={false}
                      type="8x8"
                      size={2}
                      colorSteps={2}
                      fit="cover"
                    />
                  </div>
                )}

                {/* Glass theme fluted glass effect */}
                {wantsFlutedGlass && ditherReady && ditherImageEl && (
                  <div className="absolute inset-0 pointer-events-none" style={{ padding: 0, opacity: 0.75, transform: 'scale(1.02)' }}>
                    <FlutedGlass
                      width={cardWidth}
                      height={cardHeight}
                      image={ditherImageEl}
                      colorBack="#00000000"
                      colorShadow="#000000"
                      colorHighlight="#ffffff"
                      size={0.77}
                      shadows={0.37}
                      highlights={0.28}
                      shape="lines"
                      angle={0}
                      distortionShape="cascade"
                      distortion={0.54}
                      shift={0.16}
                      stretch={0}
                      blur={0.25}
                      edges={0.52}
                      margin={0}
                      grainMixer={0}
                      grainOverlay={0}
                      scale={1}
                      fit="cover"
                    />
                  </div>
                )}

                {/* Wood theme halftone dots effect */}
                {wantsHalftoneDots && ditherReady && ditherImageEl && (
                  <div className="absolute inset-0 pointer-events-none" style={{ padding: 0, transform: 'scale(1.02)' }}>
                    <HalftoneDots
                      width={cardWidth}
                      height={cardHeight}
                      image={ditherImageEl}
                      colorBack={woodBackColor}
                      colorFront={halftoneDotColors.front}
                      originalColors={false}
                      type="gooey"
                      grid="square"
                      inverted
                      size={0.57}
                      radius={1}
                      contrast={1}
                      grainMixer={0.05}
                      grainOverlay={0.3}
                      grainSize={0.5}
                      scale={1}
                      fit="cover"
                    />
                  </div>
                )}

                <img
                  src={displaySrc}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ opacity: (wantsDither || wantsHalftoneDots) ? 0 : 1, userSelect: 'none' }}
                  draggable={false}
                  loading={card.grid_y < 10 ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={currentIndex === 0 && card.grid_y < 10 ? 'high' : 'auto'}
                  onLoad={() => {
                    const idx = currentIndex
                    LOADED_IMAGE_SRCS.add(displaySrc)
                    setHasLoadedByIndex((prev) => ({ ...prev, [idx]: true }))
                    setHasErrorByIndex((prev) => ({ ...prev, [idx]: false }))
                  }}
                  onError={() => {
                    const idx = currentIndex
                    setHasErrorByIndex((prev) => ({ ...prev, [idx]: true }))
                    setHasLoadedByIndex((prev) => ({ ...prev, [idx]: false }))
                    window.setTimeout(() => {
                      setRetryNonceByIndex((prev) => ({ ...prev, [idx]: Date.now() }))
                      setHasErrorByIndex((prev) => ({ ...prev, [idx]: false }))
                    }, 400)
                  }}
                />
              </motion.div>
            </AnimatePresence>

            {hasErrorByIndex[currentIndex] && (
              <div className="absolute inset-0 flex items-center justify-center bg-nok-gray-800/60">
                <ImageIcon size={iconSize} className="opacity-70" />
              </div>
            )}

            {effectiveImages.length > 1 && (
              <div
                className="absolute left-0 right-0 bottom-0 flex items-center justify-center"
                style={{ paddingBottom: '10px', paddingTop: '10px' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center" style={{ gap: '6px' }}>
                  {effectiveImages.map((_, i) => {
                    const active = i === currentIndex
                    return (
                      <button
                        key={i}
                        type="button"
                        aria-label={`Go to image ${i + 1}`}
                        onClick={() => setCurrentIndex(i)}
                        className="rounded-full"
                        style={{
                          width: active ? '7px' : '6px',
                          height: active ? '7px' : '6px',
                          background: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.45)',
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={iconSize} className="opacity-70" />
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {showLightbox && effectiveImages.length > 0 && (
          <PhotoLightbox
            images={effectiveImages}
            initialIndex={currentIndex}
            onClose={() => setShowLightbox(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
})

export default PhotoCard
